"use client";
import React, { createContext, useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../config/apiconfig";

export const PromoContext = createContext(null);

// Cache utilities
const CACHE_KEYS = {
  PROMO_CODES: 'promo_codes_cache',
  CACHE_TIMESTAMP: 'promo_cache_timestamp',
  CACHE_VERSION: 'promo_cache_version'
};

const CACHE_DURATION = 1 * 20 * 1000; // 20 seconds
const CACHE_VERSION = '1.2'; // Increment this to invalidate old cache (updated for product_id)

const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(key);
    const timestamp = localStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
    const version = localStorage.getItem(CACHE_KEYS.CACHE_VERSION);

    // Check cache version first
    if (version !== CACHE_VERSION) {
      clearCache();
      return null;
    }

    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_DURATION) {
        const parsedData = JSON.parse(cached);
        // Check if cached data contains default codes (indicating API failure)
        const isDefaultData = parsedData.some(item =>
          (typeof item === 'string' && item.includes('SAVE20')) ||
          (typeof item === 'object' && item.code && item.code.includes('SAVE20'))
        );

        if (isDefaultData) {
          return null;
        }

        return parsedData;
      } else {
      }
    }
  } catch (error) {
  }
  return null;
};

const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString());
    localStorage.setItem(CACHE_KEYS.CACHE_VERSION, CACHE_VERSION);
  } catch (error) {
  }
};

const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.PROMO_CODES);
    localStorage.removeItem(CACHE_KEYS.CACHE_TIMESTAMP);
    localStorage.removeItem(CACHE_KEYS.CACHE_VERSION);
  } catch (error) {
  }
};

export const PromoProvider = ({ children }) => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoCodesLoading, setPromoCodesLoading] = useState(false);
  const [promoCodesError, setPromoCodesError] = useState(null);

  // Default fallback promo codes in case API fails (now with product_id structure)
  const defaultPromoCodes = [
    {
      displayText: 'SAVE20 - 20% OFF on all orders',
      code: 'SAVE20',
      title: '20% OFF on all orders',
      description: 'Limited time offer',
      product_id: null
    },
    {
      displayText: 'FREESHIP - Free shipping on orders above ₹999',
      code: 'FREESHIP',
      title: 'Free shipping on orders above ₹999',
      description: 'No minimum order',
      product_id: null
    },
    {
      displayText: 'NEWUSER - 15% OFF for new customers',
      code: 'NEWUSER',
      title: '15% OFF for new customers',
      description: 'First time buyers',
      product_id: null
    },
    {
      displayText: 'BULK50 - 50% OFF on bulk orders',
      code: 'BULK50',
      title: '50% OFF on bulk orders',
      description: 'Wholesale pricing',
      product_id: null
    },
    {
      displayText: 'WEEKEND25 - 25% OFF weekend special',
      code: 'WEEKEND25',
      title: '25% OFF weekend special',
      description: 'Weekend only',
      product_id: null
    }
  ];

  /* ---------------- FETCH PROMO CODES ---------------- */
  const fetchPromoCodes = useCallback(async (forceRefresh = false) => {

    // Check cache first (unless forced refresh)
    if (!forceRefresh) {
      const cachedPromoCodes = getCachedData(CACHE_KEYS.PROMO_CODES);
      if (cachedPromoCodes) {
        setPromoCodes(cachedPromoCodes);
        return;
      }
    }

    setPromoCodesLoading(true);
    setPromoCodesError(null);

    try {
      const apiUrl = `${BASE_URL}/promo-codes`;

      const res = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });


      if (!res.ok) {
        throw new Error(`Failed to fetch promo codes: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();

      // Check if response has the expected structure
      if (data.status !== "200" && data.status !== 200) {
        throw new Error(`API returned error status: ${data.status}`);
      }

      const promoList = data?.data || [];

      if (!Array.isArray(promoList)) {
        setPromoCodes(defaultPromoCodes);
        return;
      }

      if (promoList.length === 0) {
        setPromoCodes(defaultPromoCodes);
        return;
      }

      // Transform API data to include all necessary fields
      // API format: { "title": "Special Christmas Offer", "description": "first Time Users Only", "code": "12345", "product_id": 1 }
      // Expected format: { displayText: "12345 - Special Christmas Offer", code: "12345", title: "...", description: "...", product_id: 1 }
      const transformedPromoCodes = promoList.map((promo) => {
        const code = promo.code || '';
        const title = promo.title || '';
        const description = promo.description || '';
        const product_id = promo.product_id || null;


        // Create the display string: "CODE - TITLE" or include description if needed
        let displayText = '';
        if (code && title) {
          displayText = `${code} - ${title}`;
        } else if (code && description) {
          displayText = `${code} - ${description}`;
        } else if (title) {
          displayText = title;
        } else {
          displayText = `${code || 'PROMO'} - Special Offer`;
        }

        return {
          displayText,
          code,
          title,
          description,
          product_id
        };
      });


      // Filter out any empty or invalid entries
      const validPromoCodes = transformedPromoCodes.filter(promo =>
        promo && promo.displayText && promo.displayText.trim().length > 0
      );


      // Use the valid promo codes from API
      const finalPromoCodes = validPromoCodes.length > 0 ? validPromoCodes : defaultPromoCodes;

      setPromoCodes(finalPromoCodes);

      // Only cache if we got real data from API (not defaults)
      if (validPromoCodes.length > 0) {
        setCachedData(CACHE_KEYS.PROMO_CODES, finalPromoCodes);
      } else {
      }

    } catch (err) {
      setPromoCodesError(err.message);

      // Use default promo codes as fallback
      setPromoCodes(defaultPromoCodes);

      // Don't cache defaults on error to allow retry
    } finally {
      setPromoCodesLoading(false);
    }
  }, []);

  /* ---------------- GET PROMO CODE BY CODE ---------------- */
  const getPromoCodeByCode = useCallback((code) => {
    if (!code || !promoCodes.length) return null;

    return promoCodes.find(promo =>
      promo.code && promo.code.toLowerCase().startsWith(code.toLowerCase())
    );
  }, [promoCodes]);

  /* ---------------- VALIDATE PROMO CODE ---------------- */
  const validatePromoCode = useCallback((code) => {
    if (!code || !promoCodes.length) return false;

    return promoCodes.some(promo =>
      promo.code && promo.code.toLowerCase().includes(code.toLowerCase())
    );
  }, [promoCodes]);

  /* ---------------- GET PROMO CODE BY PRODUCT ID ---------------- */
  const getPromoCodeByProductId = useCallback((productId) => {
    if (!productId || !promoCodes.length) return null;

    return promoCodes.find(promo =>
      promo.product_id === productId
    );
  }, [promoCodes]);

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    fetchPromoCodes(true); // Always force refresh on component mount/page reload
  }, [fetchPromoCodes]);

  // Debug effect to log state changes
  useEffect(() => {
  }, [promoCodes, promoCodesLoading, promoCodesError]);

  const contextValue = {
    // Promo codes data
    promoCodes,
    promoCodesLoading,
    promoCodesError,

    // Actions
    fetchPromoCodes,
    getPromoCodeByCode,
    validatePromoCode,
    getPromoCodeByProductId,

    // Utility
    hasPromoCodes: promoCodes.length > 0,
    promoCodesCount: promoCodes.length,

    // Default fallback
    defaultPromoCodes
  };

  return (
    <PromoContext.Provider value={contextValue}>
      {children}
    </PromoContext.Provider>
  );
};

// Custom hook for using promo context
export const usePromo = () => {
  const context = React.useContext(PromoContext);
  if (!context) {
    throw new Error('usePromo must be used within a PromoProvider');
  }
  return context;
};

// HOC for components that need promo data
export const withPromo = (Component) => {
  return function PromoWrappedComponent(props) {
    const promoData = usePromo();
    return <Component {...props} promoData={promoData} />;
  };
};
